import React, { useRef } from 'react';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons/faArrowUp';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons/faCircleExclamation';
import { faSpinner } from '@fortawesome/free-solid-svg-icons/faSpinner';
import { faUserSecret } from '@fortawesome/free-solid-svg-icons/faUserSecret';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import useAutosizeTextArea from 'buyside/util/hooks/useAutosizeTextArea';
import { useKeyPress } from 'buyside/util/hooks/useKeyPress';

import {
  CreateChatMessageInput,
  Maybe,
  useCreateChatMessageMutation
} from 'interfaces/graphql-typings';

import styles from './chatMessageFormStyles.scss';

const ChatMessageForm: React.FC<{
  canChatPublicly: boolean;
  canManageMessages: boolean;
  canViewIdeas: boolean;
}> = ({ canChatPublicly, canManageMessages, canViewIdeas }) => {
  const { chatId } = useParams();

  const textAreaRef = useRef<Maybe<HTMLTextAreaElement>>(null);

  const [handleCreateChatMessage, { error, loading }] = useCreateChatMessageMutation();

  const { handleSubmit, register, reset, watch } = useForm<CreateChatMessageInput>({
    defaultValues: { anonymous: !canChatPublicly }
  });

  const { ref, ...rest } = register('body', { required: true });

  const onSubmit = handleSubmit(({ anonymous, body }) =>
    handleCreateChatMessage({
      variables: { anonymous, body, chatId: `${chatId}` },
      onCompleted: () => reset({ anonymous, body: '' })
    })
  );

  const isDisabled = !canManageMessages || !canViewIdeas || loading;

  useAutosizeTextArea(textAreaRef, watch('body'));

  useKeyPress(onSubmit, 'Enter');

  return (
    <form id="chat-message-form" className={styles.chatMessageForm} onSubmit={onSubmit}>
      <div className={styles.submissionArea}>
        <textarea
          {...rest}
          id="body"
          disabled={isDisabled}
          placeholder="Post a message"
          ref={(e): void => {
            ref(e);
            textAreaRef.current = e;
          }}
        />
        <button id="submit" disabled={isDisabled}>
          <FontAwesomeIcon icon={loading ? faSpinner : faArrowUp} spin={loading} />
        </button>
      </div>
      <div className={styles.formFooter}>
        {!canManageMessages || !canViewIdeas ? (
          <label>
            <FontAwesomeIcon icon={faCircleExclamation} />
            You're currently unable to send chat messages.
          </label>
        ) : (
          <>
            <div className={styles.checkbox}>
              {canChatPublicly ? (
                <>
                  <input id="anonymous" type="checkbox" {...register('anonymous')} />
                  <label htmlFor="anonymous">Chat Anonymously</label>
                </>
              ) : (
                <label>
                  <FontAwesomeIcon icon={faUserSecret} />
                  You're chatting anonymously
                </label>
              )}
            </div>
          </>
        )}
        {error ? (
          <label className={styles.error}>
            <FontAwesomeIcon icon={faCircleExclamation} /> {error.graphQLErrors[0].message}
          </label>
        ) : null}
        <label className={styles.disclaimer}>
          These chats are monitored by SumZero and our community. Peers are able to anonymously flag
          comments made if they deem them inappropriate or otherwise. The SumZero team reserves the
          right to revoke live chat access from anyone that might be abusing the tool.
        </label>
      </div>
    </form>
  );
};

export default ChatMessageForm;
